import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtInt } from "@/lib/format";
import { ArrowDownToLine } from "lucide-react";

const ENTRY_TYPES = {
  production_in: "Üretim Girişi",
  ready_purchase_in: "Hazır Alım Girişi",
  return_in: "İade Girişi",
  count_excess: "Sayım Fazlası",
  manual_increase: "Manuel Düzeltme Artış",
};

export default function StockEntry() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [movementType, setMovementType] = useState("ready_purchase_in");
  const [quantity, setQuantity] = useState("");
  const [supplier, setSupplier] = useState("");
  const [docNo, setDocNo] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { is_active: true } }).then((r) => setProducts(r.data));
  }, []);

  const selected = products.find((p) => p.id === productId);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) return toast.error("Ürün seçin");
    if (!quantity || Number(quantity) <= 0) return toast.error("Geçerli miktar girin");
    setSaving(true);
    try {
      await api.post("/stock/entry", {
        product_id: productId,
        movement_type: movementType,
        quantity: Number(quantity),
        description: description || (supplier ? `Tedarikçi: ${supplier}` : ""),
        document_no: docNo,
        supplier_name: supplier,
      });
      toast.success("Stok girişi kaydedildi");
      navigate("/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-5">
      <div className="azd-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center">
            <ArrowDownToLine size={18} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-[#08285A]">Stok Girişi</h2>
            <p className="text-xs text-[#6B7280]">Stok artışı için kayıt oluşturun</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Ürün <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="entry-product">
                <SelectValue placeholder="Ürün seçin" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.product_code} — {p.product_name} ({fmtInt(p.current_stock)} {p.unit})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <div className="text-xs text-[#6B7280] mt-1">
                Mevcut stok: <b>{fmtInt(selected.current_stock)} {selected.unit}</b>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Giriş Tipi <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={movementType} onValueChange={setMovementType}>
              <SelectTrigger data-testid="entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ENTRY_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Miktar ({selected?.unit || "-"}) <span className="text-[#DC2626]">*</span>
            </label>
            <Input data-testid="entry-quantity" type="number" min="0.01" step="0.01"
                   value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Tedarikçi</label>
            <Input data-testid="entry-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Belge No</label>
            <Input data-testid="entry-doc-no" value={docNo} onChange={(e) => setDocNo(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Açıklama</label>
            <Textarea data-testid="entry-description" value={description}
                      onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
        </div>

        {selected && quantity && (
          <div className="mt-5 p-4 bg-[#F6F8FB] rounded-lg border border-[#E5E7EB]">
            <div className="text-xs text-[#6B7280] uppercase tracking-wider font-bold mb-1">
              Yeni Stok Tahmini
            </div>
            <div className="text-2xl font-extrabold text-[#16A34A]">
              {fmtInt(Number(selected.current_stock) + Number(quantity || 0))} {selected.unit}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/products")}>Vazgeç</Button>
        <Button type="submit" disabled={saving} data-testid="save-entry"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : "Stok Girişini Kaydet"}
        </Button>
      </div>
    </form>
  );
}
